import { useContext, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import FloatingWindow from "../../genericComponents/FloatingWindow/FloatingWindow";

import PinIcon from "../../../assets/pin.svg?react";

import Graphic from "@arcgis/core/Graphic";
import PictureMarkerSymbol from "@arcgis/core/symbols/PictureMarkerSymbol.js";
import pinIcon from "../../../assets/pin.svg"

import { setBoatLocationWindowOpen } from "../../../store/appSlice";
import { getMapView } from "../../../utils/arcgisUtils";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import NfsButton from "../../genericComponents/NfsButton/NfsButton";
import { toast } from "react-toastify";
import Toggle from "../../genericComponents/Toggle/Toggle";
import { ExpandedNmeaData } from "../../../types/types";
import { uuidv4 } from "../../../utils/helpers";
import { UserContext } from "../../../Context";
import Connector from "../../../utils/signalr";
import Point from "@arcgis/core/geometry/Point";
import NfsDropdown from "../../genericComponents/NfsDropdown/NfsDropdown";



const BoatLocation = (props: { boatLocationWindowOpen: boolean }) => {
    const userContext = useContext(UserContext);
    const userName = userContext?.user.value?.username;
    const { connection } = Connector(userName ?? uuidv4());


    const boatLocationWindowRef = useRef(document.createElement("div"));
    const dispatch = useDispatch();
    const [hasBoatLocation, setHasBoatLocation] = useState(false);
    const [boatVisible, setBoatVisible] = useState(true);
    const [latestNmeaInfo, setLatestNmeaInfo] = useState<ExpandedNmeaData | null>(null);

    const [selectedSourceIdForViewing, setSelectedSourceIdForViewing] = useState<string | null>(null);
    const [boatsLocationsHistory, setBoatsLocationsHistory] = useState<{ [key: string]: ExpandedNmeaData[] }>({});
    const boatsLocationsHistoryRef = useRef(boatsLocationsHistory);

    const closeWindow = () => {
        dispatch(setBoatLocationWindowOpen(false));
    };

    const zoomToBoatLocation = () => {
        const mapView = getMapView();
        if (!mapView?.map) return;

        let boatLocationLayer = mapView.map.findLayerById("boatCoordinates") as GraphicsLayer;

        if (!boatLocationLayer || boatLocationLayer.graphics.length === 0) {
            toast.error("Ingen båtlokasjon funnet");
            return;
        }

        const graphics = boatLocationLayer.graphics.toArray();
        if (graphics.length > 0) {
            const boatLocation = graphics[0].geometry;
            mapView.goTo({ target: boatLocation ?? undefined, zoom: 12 }).catch((error) => {
                console.error("Error zooming to boat location:", error);
                toast.error("Kunne ikke zoome til båtlokasjon");
            });
        } else {
            toast.error("Ingen båtlokasjon funnet");
        }
    }

    const toggleBoatVisibility = () => {
        const boatLocationLayer = getMapView()?.map?.findLayerById("boatCoordinates") as GraphicsLayer;
        if (boatLocationLayer) {
            boatLocationLayer.visible = !boatLocationLayer.visible;
            setBoatVisible(boatLocationLayer.visible);
        }
    };

    const drawBoatInMap = () => {
        if (!selectedSourceIdForViewing || !boatsLocationsHistory[selectedSourceIdForViewing] || boatsLocationsHistory[selectedSourceIdForViewing].length === 0) {
            setHasBoatLocation(false);
            return;
        }
        const locationInfo = boatsLocationsHistory[selectedSourceIdForViewing][boatsLocationsHistory[selectedSourceIdForViewing].length - 1];
        setLatestNmeaInfo(locationInfo);
        const view = getMapView();
        if (view && locationInfo.Latitude && locationInfo.Longitude) {
            let boatCoordinatesLayer = view.map?.findLayerById("boatCoordinates") as GraphicsLayer;
            if (!boatCoordinatesLayer) {
                return;
            }
            boatCoordinatesLayer.removeAll();
            const point = new Point({
                longitude: locationInfo.Longitude,
                latitude: locationInfo.Latitude,
                spatialReference: { wkid: 4326 },
            });
            const boatSymbol = new PictureMarkerSymbol({
                url: pinIcon,
                width: "32px",
                height: "32px",
                yoffset: 16, // Adjust the y-offset to center the icon on the point
            });
            const dateString = new Date().toLocaleString();
            const pointGraphic = new Graphic({
                geometry: point,
                symbol: boatSymbol,
                attributes: {
                    Date: dateString,
                    Latitude: locationInfo.Latitude,
                    Longitude: locationInfo.Longitude,
                    Heading: locationInfo.Heading,
                    HorizontalPrecision: locationInfo.HorizontalPrecision,
                    Altitude: locationInfo.Altitude,
                    NumberOfSatellites: locationInfo.NumberOfSatellites,
                    Speed: locationInfo.Speed,
                    RawNmeaMessage: locationInfo.RawNmeaMessage,
                },
                popupTemplate: {
                    title: "Båt",
                    content: `Sist oppdatert ${dateString} <br> Fart: ${locationInfo.Speed ?? "ikke oppgitt"
                        } knop <br> Kurs: ${locationInfo.Heading ?? "ikke oppgitt"} grader <br> Horisontal presisjon: ${locationInfo.HorizontalPrecision ?? "ikke oppgitt"
                        } meter <br> Høyde: ${locationInfo.Altitude ?? "ikke oppgitt"} meter <br> Antall satellitter: ${locationInfo.NumberOfSatellites ?? "ikke oppgitt"
                        }`,
                },
            });
            boatCoordinatesLayer.add(pointGraphic);
        }
    };

    const storeReceivedBoatLocation = (locationInfo: ExpandedNmeaData, sourceId: string) => {
        const locationHistory = { ...boatsLocationsHistoryRef.current };
        if (!locationHistory[sourceId]) {
            locationHistory[sourceId] = [];
        }
        locationHistory[sourceId].push(locationInfo);
        if (locationHistory[sourceId].length > 10) {
            locationHistory[sourceId].shift();
        }
        setBoatsLocationsHistory(locationHistory);
    }

    useEffect(() => {
        connection.on("ReceiveMessage", handleReceiveMessage);

        return () => {
            connection.off("ReceiveMessage", handleReceiveMessage);
        };

        function handleReceiveMessage(_sourceClientID: string, messageType: string, message: string) {
            if (messageType === "LocationUpdate") {
                const locationInfo: ExpandedNmeaData = JSON.parse(message);
                locationInfo.Date = new Date().toLocaleString();
                storeReceivedBoatLocation(locationInfo, _sourceClientID);
            }
        }
    }, [connection]);

    useEffect(() => {
        if (selectedSourceIdForViewing === null) {
            setLatestNmeaInfo(null);
            const view = getMapView();
            if (view?.map) {
                let boatCoordinatesLayer = view.map?.findLayerById("boatCoordinates") as GraphicsLayer;
                if (boatCoordinatesLayer) {
                    boatCoordinatesLayer.removeAll();
                }
            }
        }
        boatsLocationsHistoryRef.current = boatsLocationsHistory;
        drawBoatInMap();
        if (boatsLocationsHistory && Object.keys(boatsLocationsHistory).length > 0) {
            setHasBoatLocation(true);
        } else {
            setHasBoatLocation(false);
        }
    }, [boatsLocationsHistory, selectedSourceIdForViewing]);

    const calcHeightAndWidth = () => {
        const heightAndWidth = { x: 0, y: 0 };
        const windowWidth = window.innerWidth;
        heightAndWidth.x = Math.max(windowWidth - 600, 200);
        heightAndWidth.y = 40;
        return heightAndWidth;
    }

    return (
        <FloatingWindow
            title="Delt lokasjon"
            className={`boat-location-window ${props.boatLocationWindowOpen ? "" : "hidden"}`}
            toolRef={boatLocationWindowRef}
            closeWindow={closeWindow}
            defaultPosition={calcHeightAndWidth()}
            Icon={PinIcon}
            width={400}
        >
            <div>
                <span className="flex-row align-center small-gap">
                    {!hasBoatLocation && (
                        <>
                            <span>Ingen delt lokasjon funnet</span>
                            {/* <NfsButton className="no-flex" outlined onClick={initVariables}>Refresh</NfsButton> */}
                        </>
                    )}
                </span>
                {hasBoatLocation &&
                    <div className="flex-col small-gap">

                        <div className="flex-row space-between medium-gap">
                            <div className="flex-row small-gap align-center">
                                <NfsButton className="no-flex" onClick={zoomToBoatLocation} >
                                    Gå til lokasjon
                                </NfsButton>
                                {/* <NfsButton className="no-flex" outlined onClick={initVariables}>Refresh</NfsButton> */}
                            </div>
                            <div className="flex-row small-gap align-center">
                                <span>Vis delt lokasjon i kartet</span>
                                <Toggle checked={boatVisible} onChange={toggleBoatVisibility} />
                            </div>
                        </div>
                        <NfsDropdown
                            options={Object.keys(boatsLocationsHistory)}
                            selectedOption={selectedSourceIdForViewing}
                            setSelectedOption={(option) => { setSelectedSourceIdForViewing(option) }}
                            placeholder="Velg kilde"
                            allowEmpty
                        />
                        {latestNmeaInfo && (
                            <div className="boat-location-info flex-col small-gap">
                                <p style={{ margin: 0 }}><strong>Tidspunkt:</strong> {latestNmeaInfo.Date ?? "Ikke oppgitt"}</p>
                                <p style={{ margin: 0 }}><strong>Latitude:</strong> {latestNmeaInfo.Latitude ?? "Ikke oppgitt"}</p>
                                <p style={{ margin: 0 }}><strong>Longitude:</strong> {latestNmeaInfo.Longitude ?? "Ikke oppgitt"}</p>
                                <p style={{ margin: 0 }}><strong>Heading:</strong> {latestNmeaInfo.Heading ?? "Ikke oppgitt"}</p>
                                <p style={{ margin: 0 }}><strong>Horisontal presisjon:</strong> {latestNmeaInfo.HorizontalPrecision ?? "Ikke oppgitt"}</p>
                                <p style={{ margin: 0 }}><strong>Høyde:</strong> {latestNmeaInfo.Altitude ?? "Ikke oppgitt"}</p>
                                <p style={{ margin: 0 }}><strong>Antall satellitter:</strong> {latestNmeaInfo.NumberOfSatellites ?? "Ikke oppgitt"}</p>
                                <p style={{ margin: 0 }}><strong>Hastighet:</strong> {latestNmeaInfo.Speed ?? "Ikke oppgitt"}</p>
                                <p style={{ margin: 0 }} className="break-all"><strong>NMEA-melding:</strong> {latestNmeaInfo.RawNmeaMessage ?? "Ikke oppgitt"}</p>
                            </div>
                        )}
                    </div>
                }
            </div>
        </FloatingWindow >
    );
};

export default BoatLocation;
